import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, ArrowLeft, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const KIND_LABELS = {
  registration: 'Sign-up consents', consent_gate: 'Consents confirmed', payment_terms: 'Plan terms accepted at payment',
  profile_publish_declaration: 'Profile accuracy & authority declaration', marketing_opt_in: 'Marketing updates turned on',
  marketing_opt_out: 'Marketing updates turned off', cookies_accepted: 'Cookies accepted', cookies_rejected: 'Cookies rejected',
  recording_studio: 'Recording consent', data_processing_withdrawal_request: 'Data consent withdrawn / deletion requested',
};
const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Not recorded');

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-zinc-800 last:border-0">
    <span className="text-zinc-300 text-sm">{label}</span>
    <span className="text-zinc-400 text-sm text-right" data-testid={testId}>{value}</span>
  </div>
);

export default function MyConsentsPage() {
  const [data, setData] = useState(null);

  const load = () => axios.get(`${API_URL}/api/me/consents`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => toast.error('Please log in'));
  useEffect(() => { load(); }, []);

  const toggleMarketing = async () => {
    await axios.put(`${API_URL}/api/me/consents/marketing`, { marketing: !data.marketing }, { withCredentials: true });
    toast.success(!data.marketing ? 'Marketing updates turned on' : 'Marketing updates turned off');
    load();
  };

  const withdraw = async () => {
    if (!window.confirm('Withdrawing consent means we will stop processing your data and delete your account and profile. Continue?')) return;
    const r = await axios.post(`${API_URL}/api/me/consents/withdraw`, {}, { withCredentials: true });
    toast.success(r.data.message);
    load();
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white pt-28 pb-16 px-6" data-testid="my-consents-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-8"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <h1 className="text-4xl font-bold mb-2 flex items-center gap-3"><ShieldCheck className="w-8 h-8 text-[#C41E3A]" /> Privacy & consents</h1>
        <p className="text-zinc-400 mb-8">See what you've agreed to and manage your choices, as provided under the DPDP Act 2023 and GDPR.</p>
        {data && (
          <>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl px-5 mb-6">
              <Row label="Terms of Service & Privacy Policy" value={fmt(data.terms_accepted_at)} testId="consent-terms-date" />
              <Row label="Data processing consent" value={fmt(data.data_processing_at)} testId="consent-data-date" />
              <Row label="Video recording declaration" value={fmt(data.video_declaration_accepted_at)} testId="consent-video-date" />
              <div className="flex items-center justify-between gap-4 py-3">
                <span className="text-zinc-300 text-sm">Marketing updates</span>
                <button type="button" onClick={toggleMarketing} className={`px-3 py-1.5 rounded-full text-xs font-medium ${data.marketing ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-400'}`} data-testid="consent-marketing-toggle">
                  {data.marketing ? 'On, click to turn off' : 'Off, click to turn on'}
                </button>
              </div>
            </div>
            <h2 className="text-base md:text-lg font-semibold mb-3">Consent history</h2>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl px-5 mb-8" data-testid="consent-history">
              {data.history.length === 0 && <p className="py-4 text-sm text-zinc-500">No consent records yet.</p>}
              {data.history.map((h, i) => (
                <div key={i} className="flex items-center justify-between gap-4 py-3 border-b border-zinc-800 last:border-0 text-sm">
                  <span className="text-zinc-300 flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" />{KIND_LABELS[h.kind] || h.kind}</span>
                  <span className="text-zinc-500">{fmt(h.at)}</span>
                </div>
              ))}
            </div>
            {data.withdrawal_requested_at ? (
              <p className="text-amber-400 text-sm flex items-center gap-2" data-testid="consent-withdrawal-pending"><AlertTriangle className="w-4 h-4" /> Withdrawal requested on {fmt(data.withdrawal_requested_at)}. Our team will complete it within 30 days.</p>
            ) : (
              <button type="button" onClick={withdraw} className="text-sm text-red-400 hover:text-red-300 underline" data-testid="consent-withdraw-btn">Withdraw data consent and request deletion</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
